import { z } from 'zod';

import { SentimentBandSchema } from '../aspect-theme.js';
import { FeedbackFormFieldKindSchema } from './field-schema.js';

// Submission (camelCase + row variant) ------------------------------------

// Snapshot of the field set captured at submission time so the read path
// stays correct after a later replace_feedback_form_fields rewrites the
// field rows. Older rows pre-dating migration 0033 are NULL; the read
// path falls back to the joined current fields when that's the case.
//
// kind/config were added to the snapshot in migration 0108 so the
// quantitative summary can interpret each answer without joining the live
// field rows. Both are optional: rows written before 0108 carry only
// {id,title,orderIndex} and the read RPC falls back to the live field.
export const FeedbackFormSubmissionFieldSnapshotEntrySchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  orderIndex: z.number().int().nonnegative(),
  kind: FeedbackFormFieldKindSchema.optional(),
  config: z.record(z.unknown()).optional(),
});
export type FeedbackFormSubmissionFieldSnapshotEntry = z.infer<
  typeof FeedbackFormSubmissionFieldSnapshotEntrySchema
>;

export const FeedbackFormSubmissionSchema = z.object({
  id: z.string().uuid(),
  formId: z.string().uuid(),
  gameId: z.string().uuid(),
  studioId: z.string().uuid(),
  feedbackPoolId: z.string().uuid().nullable(),
  // Null in anonymous mode. The bot is the enforcement point - see
  // .docs/feedback-forms/bot-interactions.md (Anonymous mode section).
  submitterExternalId: z.string().nullable(),
  submitterHandle: z.string().nullable(),
  // Keyed by field UUID. Values are strings (text/select/severity/binary)
  // or numbers (rating). Render via fieldSnapshot when available;
  // otherwise the portal joins the parent form's current field list to
  // recover the question text.
  values: z.record(z.union([z.string(), z.number()])),
  fieldSnapshot: z.array(FeedbackFormSubmissionFieldSnapshotEntrySchema).nullable(),
  discordInteractionId: z.string().nullable(),
  submittedAt: z.string().datetime({ offset: true }),
});
export type FeedbackFormSubmission = z.infer<typeof FeedbackFormSubmissionSchema>;

// Row variant for the supabase-js read path. The snapshot column lives as
// snake_case in the DB (`order_index`); we map it to the canonical
// camelCase entry shape inline so the two snapshot schemas don't drift.
const FieldSnapshotRowEntrySchema = z
  .object({
    id: z.string().uuid(),
    title: z.string(),
    order_index: z.number().int().nonnegative(),
    kind: FeedbackFormFieldKindSchema.optional(),
    config: z.record(z.unknown()).optional(),
  })
  .transform(
    (row): FeedbackFormSubmissionFieldSnapshotEntry => ({
      id: row.id,
      title: row.title,
      orderIndex: row.order_index,
      kind: row.kind,
      config: row.config,
    }),
  );

export const FeedbackFormSubmissionRowSchema = z
  .object({
    id: z.string().uuid(),
    form_id: z.string().uuid(),
    game_id: z.string().uuid(),
    studio_id: z.string().uuid(),
    feedback_pool_id: z.string().uuid().nullable(),
    submitter_external_id: z.string().nullable(),
    submitter_handle: z.string().nullable(),
    values: z.record(z.union([z.string(), z.number()])),
    field_snapshot: z.array(FieldSnapshotRowEntrySchema).nullable(),
    discord_interaction_id: z.string().nullable(),
    submitted_at: z.string().datetime({ offset: true }),
  })
  .transform((row) => ({
    id: row.id,
    formId: row.form_id,
    gameId: row.game_id,
    studioId: row.studio_id,
    feedbackPoolId: row.feedback_pool_id,
    submitterExternalId: row.submitter_external_id,
    submitterHandle: row.submitter_handle,
    values: row.values,
    fieldSnapshot: row.field_snapshot,
    discordInteractionId: row.discord_interaction_id,
    submittedAt: row.submitted_at,
  })) satisfies z.ZodType<FeedbackFormSubmission, z.ZodTypeDef, unknown>;

// --- Derived submission sentiment (migration 0106) -------------------------
//
// Read-side aggregates of the existing per-record sentiment enrichments.
// No new write path; these mirror the RPC return rows verbatim (snake_case
// from PostgREST) and expose a camelCase shape the portal consumes.

// One row of get_form_submission_sentiments(uuid[]). score/band are null
// when the submission has neither a current sentiment enrichment nor any
// rating fields to fall back on.
export const FormSubmissionSentimentRowSchema = z
  .object({
    submission_id: z.string().uuid(),
    score: z.number().nullable(),
    band: SentimentBandSchema.nullable(),
  })
  .transform((row) => ({
    submissionId: row.submission_id,
    score: row.score,
    band: row.band,
  }));
export type FormSubmissionSentiment = z.infer<
  typeof FormSubmissionSentimentRowSchema
>;

// get_form_sentiment_summary(uuid) - one row. Counts arrive as strings
// from PostgREST (bigint), so coerce. mean_score/band are null when no
// submission scored.
export const FormSentimentSummaryRowSchema = z
  .object({
    submission_count: z.coerce.number().int().nonnegative(),
    scored_count: z.coerce.number().int().nonnegative(),
    mean_score: z.number().nullable(),
    band: SentimentBandSchema.nullable(),
    positive_count: z.coerce.number().int().nonnegative(),
    neutral_count: z.coerce.number().int().nonnegative(),
    negative_count: z.coerce.number().int().nonnegative(),
  })
  .transform((row) => ({
    submissionCount: row.submission_count,
    scoredCount: row.scored_count,
    meanScore: row.mean_score,
    band: row.band,
    positiveCount: row.positive_count,
    neutralCount: row.neutral_count,
    negativeCount: row.negative_count,
  }));
export type FormSentimentSummary = z.infer<
  typeof FormSentimentSummaryRowSchema
>;

// --- Per-field quantitative summary (migration 0108) -----------------------
//
// get_form_field_summary(uuid) returns a single jsonb document aggregating
// every submission of one form into one series per question, grouped by
// (kind, title) so a question keeps one breakdown across form edits. Series
// no longer on the live form are flagged `retired`. Counts arrive as JSON
// numbers (built inside jsonb_build_object), so no coercion is needed here.
// The portal renders a chart per series: a distribution histogram for
// rating/nps, option/label bars for select/severity, a split for binary,
// and a bare response count for open-text questions.

const FieldDistributionBucketSchema = z.object({
  value: z.number().int(),
  count: z.number().int().nonnegative(),
});

// rating (1-5) and nps (1-10) share a shape: a mean plus an integer-bucket
// distribution. mean is null when no numeric answers were recorded.
export const RatingFieldSummarySchema = z.object({
  mean: z.number().nullable(),
  distribution: z.array(FieldDistributionBucketSchema),
});
export type RatingFieldSummary = z.infer<typeof RatingFieldSummarySchema>;

export const SelectFieldSummarySchema = z.object({
  options: z.array(
    z.object({
      label: z.string(),
      count: z.number().int().nonnegative(),
    }),
  ),
});
export type SelectFieldSummary = z.infer<typeof SelectFieldSummarySchema>;

export const SeverityFieldSummarySchema = z.array(
  z.object({
    label: z.string(),
    count: z.number().int().nonnegative(),
  }),
);
export type SeverityFieldSummary = z.infer<typeof SeverityFieldSummarySchema>;

export interface BinaryFieldSummary {
  trueCount: number;
  falseCount: number;
}

// Camel-cased series the portal consumes. Exactly one of the kind-specific
// payloads is populated (matching `kind`); open-text questions carry their
// answers in `responses` (newest first) instead.
export interface FormFieldSeries {
  kind: z.infer<typeof FeedbackFormFieldKindSchema>;
  title: string;
  retired: boolean;
  orderIndex: number;
  responseCount: number;
  rating: RatingFieldSummary | null;
  nps: RatingFieldSummary | null;
  select: SelectFieldSummary | null;
  binary: BinaryFieldSummary | null;
  severity: SeverityFieldSummary | null;
  // short_text / long_text only: every non-blank answer, newest first.
  responses: string[] | null;
}

const FieldSummaryRowSchema = z
  .object({
    kind: FeedbackFormFieldKindSchema,
    title: z.string(),
    retired: z.boolean(),
    order_index: z.number().int(),
    response_count: z.number().int().nonnegative(),
    rating: RatingFieldSummarySchema.optional(),
    nps: RatingFieldSummarySchema.optional(),
    select: SelectFieldSummarySchema.optional(),
    binary: z
      .object({
        true_count: z.number().int().nonnegative(),
        false_count: z.number().int().nonnegative(),
      })
      .optional(),
    severity: SeverityFieldSummarySchema.optional(),
    responses: z.array(z.string()).optional(),
  })
  .transform(
    (row): FormFieldSeries => ({
      kind: row.kind,
      title: row.title,
      retired: row.retired,
      orderIndex: row.order_index,
      responseCount: row.response_count,
      rating: row.rating ?? null,
      nps: row.nps ?? null,
      select: row.select ?? null,
      binary:
        row.binary === undefined
          ? null
          : { trueCount: row.binary.true_count, falseCount: row.binary.false_count },
      severity: row.severity ?? null,
      responses: row.responses ?? null,
    }),
  );

export const FormFieldSummaryRowSchema = z
  .object({
    submission_count: z.number().int().nonnegative(),
    fields: z.array(FieldSummaryRowSchema),
  })
  .transform((row) => ({
    submissionCount: row.submission_count,
    fields: row.fields,
  }));
export type FormFieldSummary = z.infer<typeof FormFieldSummaryRowSchema>;

// --- Game-wide sentiment summary (migration 0111) --------------------------
//
// Powers the Community Feedback summary on the Feedback screen. One row per
// source that has scored records (discord_channel / form / poll; playtests
// excluded); the portal sums the rows for its active source filter. Counts
// arrive as bigint strings from PostgREST, so coerce.

export const GameSentimentSummaryRowSchema = z
  .object({
    source: z.enum(['discord_channel', 'form', 'poll']),
    scored_count: z.coerce.number().int().nonnegative(),
    positive_count: z.coerce.number().int().nonnegative(),
    neutral_count: z.coerce.number().int().nonnegative(),
    negative_count: z.coerce.number().int().nonnegative(),
    scored_this_week: z.coerce.number().int().nonnegative(),
  })
  .transform((row) => ({
    source: row.source,
    scoredCount: row.scored_count,
    positiveCount: row.positive_count,
    neutralCount: row.neutral_count,
    negativeCount: row.negative_count,
    scoredThisWeek: row.scored_this_week,
  }));
export type GameSentimentSummaryRow = z.infer<typeof GameSentimentSummaryRowSchema>;
